import { NIVEL_EXIBIDO_UI } from '@/ui/tokens';
import { codigoQualificado } from '@/domain/instrumentos/upm-nome';
import type { TiltResumo } from '@/domain/dashboard';
import type { UltimaLeitura } from './useM1Data';

interface Props {
  instrumentosNormais: { normais: number; total: number };
  tiltResumo: TiltResumo;
  eventosUlt24h: number;
  lastReading: UltimaLeitura | null;
}

function StatCard({ value, label, sub, color }: { value: string; label: string; sub: string; color?: string }) {
  return (
    <div className="rounded-lg border border-jf-border bg-jf-surface p-3 text-center">
      <div className="text-2xl font-black leading-none" style={color ? { color } : undefined}>{value}</div>
      <div className="text-[11px] text-jf-muted mt-1">{label}</div>
      <div className="text-[10px] text-jf-dim mt-0.5">{sub}</div>
    </div>
  );
}

/**
 * Stat cards do dashboard (GUPM-191 D4-D7, fiel ao grid-4 do V8): instrumentos
 * normais (nn/nn, verde de cobertura), tiltímetros (pct, cor por condição), última
 * leitura ALTERADA (empilhada, cor por nível) e eventos 24h (amarelo). SEM card de
 * sirenes — o V8 não monitora sirenes no dashboard (sistema externo Tecal).
 */
export default function StatCards({ instrumentosNormais, tiltResumo, eventosUlt24h, lastReading }: Props) {
  const { normais, total } = instrumentosNormais;
  const tiltOk = tiltResumo.total > 0 && tiltResumo.ok === tiltResumo.total;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
      {/* D4 — instrumentos normais nn/nn: verde de "cobertura" (fixo no V8). */}
      <StatCard
        value={`${normais}/${total}`}
        label="Instrumentos normais"
        sub="cobertura de instrumentação"
        color="#22c55e"
      />
      {/* D5 — tiltímetros: pct + ok/total; cor por condição (verde OK / laranja senão, nunca amarelo). */}
      <StatCard
        value={`${tiltResumo.pct}%`}
        label={tiltOk ? 'Tiltímetros OK' : 'Tiltímetros'}
        sub={`${tiltResumo.ok}/${tiltResumo.total} operacionais`}
        color={tiltOk ? '#22c55e' : tiltResumo.total > 0 ? '#f97316' : undefined}
      />
      {/* D6 — última leitura ALTERADA. GUPM-325: o DADO na linha grande, no mesmo
          `text-2xl` dos outros três cards da fileira; o instrumento embaixo, QUALIFICADO
          com a barragem; o rótulo por último. Antes o número grande era o código do
          instrumento e o valor medido ia para a terceira linha, em 10px cinza — o card
          desviava da leitura dos vizinhos e `TIL-02` sozinho é ambíguo (o código se
          repete nas três barragens). */}
      <div data-testid="stat-ultima-leitura" className="rounded-lg border border-jf-border bg-jf-surface p-3 text-center">
        <div
          data-testid="ultima-leitura-valor"
          className="text-2xl font-black leading-none"
          style={lastReading ? { color: NIVEL_EXIBIDO_UI[lastReading.level].color } : undefined}
        >
          {lastReading ? (
            <>
              {lastReading.value}
              {lastReading.unit && <span className="ml-1 text-base font-bold">{lastReading.unit}</span>}
            </>
          ) : (
            '—'
          )}
        </div>
        <div data-testid="ultima-leitura-instrumento" className="text-[11px] text-jf-muted mt-1">
          {lastReading ? codigoQualificado(lastReading.damCode, lastReading.code) : 'sem dados'}
        </div>
        <div className="text-[10px] text-jf-dim mt-0.5">Última leitura</div>
      </div>
      {/* D7 — eventos (24h) = leituras inconsistentes: amarelo (fixo no V8). */}
      <StatCard
        value={`${eventosUlt24h}`}
        label="Eventos (24h)"
        sub="leituras inconsistentes"
        color="#eab308"
      />
    </div>
  );
}
